import {
  ActionEvents,
  CalendarEventEditScope,
  CalendarEventRef,
} from '@griply/types';
import { getConnectionProvider, getExternalEventEditable } from '@griply/utils';
import { useExternalCalendarConnections } from 'features/external-connections';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PenEdit16, Trash16 } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useDeleteExternalCalendarEventMutation } from 'shared/hooks/queries/use-delete-external-calendar-event-mutation';
import { useTrackEvents } from 'shared/hooks/use-track-events';

export type ExternalEventContextMenuProps = {
  eventTarget: CalendarEventRef;
  isRecurring: boolean;
  location: { top: number; left: number };
  // owned by the event card, which is the only place that can seed the popup
  onEdit: () => void;
  onClose: () => void;
};

export const ExternalEventContextMenu: React.FC<
  ExternalEventContextMenuProps
> = ({ eventTarget, isRecurring, location, onEdit, onClose }) => {
  const { t } = useTranslation();
  const track = useTrackEvents();
  const connections = useExternalCalendarConnections();
  const { mutate: deleteEvent } = useDeleteExternalCalendarEventMutation();

  // Same gate mobile's detail screen applies. The organiser half cannot be checked here — the
  // events list carries no `isOrganizer` — so an invitee's delete still fails at the provider;
  // this closes the half that is knowable without a per-right-click fetch.
  const canDelete = getExternalEventEditable(eventTarget, { connections });

  const onSelectEdit = () => {
    onEdit();
    onClose();
  };

  const deleteWithEditScope = (editScope: CalendarEventEditScope) => {
    deleteEvent({ ...eventTarget, editScope });
    track(ActionEvents.CalendarEventDelete, {
      provider: getConnectionProvider(connections, eventTarget.connectionId),
      is_recurring: isRecurring,
      edit_scope: editScope,
    });
    onClose();
  };

  const onDelete = () => deleteWithEditScope(CalendarEventEditScope.Single);
  const onDeleteSeries = () =>
    deleteWithEditScope(CalendarEventEditScope.Series);

  return (
    <PopupMenu location={location} onOutside={onClose}>
      <PopupMenuList>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onSelectEdit}
            start={<Icon icon={PenEdit16} />}
          >
            {t('context-menus.external-event.labels.edit')}
          </PopupMenuButton>
        </PopupMenuListItem>
        {canDelete && !isRecurring && (
          <PopupMenuListItem>
            <PopupMenuButton onClick={onDelete} start={<Icon icon={Trash16} />}>
              {t('context-menus.external-event.labels.delete')}
            </PopupMenuButton>
          </PopupMenuListItem>
        )}
        {canDelete && isRecurring && (
          <PopupMenuListItem>
            <PopupMenuButton onClick={onDelete} start={<Icon icon={Trash16} />}>
              {t('context-menus.external-event.labels.delete-single')}
            </PopupMenuButton>
          </PopupMenuListItem>
        )}
        {canDelete && isRecurring && (
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={onDeleteSeries}
              start={<Icon icon={Trash16} />}
            >
              {t('context-menus.external-event.labels.delete-series')}
            </PopupMenuButton>
          </PopupMenuListItem>
        )}
      </PopupMenuList>
    </PopupMenu>
  );
};
